import React, { useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, MapPin, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiCall } from '../utils/api';
import AddressAutocomplete from './AddressAutocomplete';

/**
 * Reusable US mailing-address input with free OpenStreetMap-powered
 * autocomplete + verification (replaces the old Lob integration).
 *
 * Behavior:
 *   - 5 structured fields (street, apt, city, state, zip)
 *   - "Verify" button calls POST /api/verify-address
 *   - Opens a side-by-side dialog: keep original vs accept standardized
 *   - If Lob says undeliverable, the dialog still lets the user save the
 *     original (soft-warn — never hard-blocks)
 *
 * Value/onChange contract:
 *   value = { primary_line, secondary_line, city, state, zip_code }
 *   onChange(nextValue) — pass the user-chosen address up to the parent form.
 *
 * Props:
 *   colorScheme: 'violet' | 'teal' | 'indigo' (matches role branding)
 *   compact: render with smaller spacing for embedded use
 *   required: shows asterisk on labels
 *   onVerificationResult: optional callback (result) — parent can store the
 *     Lob result for future audit (lat/lng, lob_id, deliverability).
 */
export default function AddressVerifier({
  value = {},
  onChange,
  colorScheme = 'violet',
  required = false,
  compact = false,
  testIdPrefix = 'address',
  onVerificationResult,
}) {
  const [verifying, setVerifying] = useState(false);
  const [dialogState, setDialogState] = useState(null); // { result, original }

  const COLORS = {
    violet: { ring: 'focus:ring-violet-500 focus:border-violet-500', btn: 'bg-violet-600 hover:bg-violet-700', accent: 'text-violet-700 bg-violet-50 border-violet-200' },
    teal: { ring: 'focus:ring-teal-500 focus:border-teal-500', btn: 'bg-teal-600 hover:bg-teal-700', accent: 'text-teal-700 bg-teal-50 border-teal-200' },
    indigo: { ring: 'focus:ring-indigo-500 focus:border-indigo-500', btn: 'bg-indigo-600 hover:bg-indigo-700', accent: 'text-indigo-700 bg-indigo-50 border-indigo-200' },
    blue: { ring: 'focus:ring-blue-500 focus:border-blue-500', btn: 'bg-blue-600 hover:bg-blue-700', accent: 'text-blue-700 bg-blue-50 border-blue-200' },
  }[colorScheme] || { ring: 'focus:ring-violet-500 focus:border-violet-500', btn: 'bg-violet-600 hover:bg-violet-700', accent: 'text-violet-700 bg-violet-50 border-violet-200' };

  const update = (patch) => onChange?.({ ...value, ...patch });

  const handleVerify = async () => {
    const { primary_line, city, state, zip_code } = value || {};
    if (!primary_line || !city || !state || !zip_code) {
      toast.error('Please fill in street, city, state, and ZIP before verifying.');
      return;
    }
    setVerifying(true);
    try {
      const result = await apiCall('/api/verify-address', {
        method: 'POST',
        body: JSON.stringify({
          primary_line: primary_line.trim(),
          secondary_line: (value.secondary_line || '').trim(),
          city: city.trim(),
          state: state.trim().toUpperCase().slice(0, 2),
          zip_code: zip_code.trim(),
        }),
      });
      onVerificationResult?.(result);
      setDialogState({ result, original: result.original });
    } catch (e) {
      toast.error(e?.message || 'Verification failed — you can still save the address as-is.');
    } finally {
      setVerifying(false);
    }
  };

  const acceptStandardized = () => {
    update(dialogState.result.standardized);
    toast.success('Address standardized.');
    setDialogState(null);
  };

  const keepOriginal = () => {
    toast('Saved as entered.', { description: 'You can re-verify anytime.' });
    setDialogState(null);
  };

  const gap = compact ? 'space-y-2' : 'space-y-3';
  const pad = compact ? 'px-3 py-2' : 'px-3 py-2.5';

  return (
    <div className={gap} data-testid={`${testIdPrefix}-verifier`}>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1">
          Street address {required && <span className="text-red-500">*</span>}
        </label>
        <AddressAutocomplete
          value={value.primary_line || ''}
          onChange={(text) => update({ primary_line: text })}
          onSelect={(s) => update({
            primary_line: s.street || s.label,
            ...(s.city ? { city: s.city } : {}),
            ...(s.state ? { state: s.state } : {}),
            ...(s.zip ? { zip_code: s.zip } : {}),
          })}
          className={`w-full ${pad} border border-slate-300 rounded-lg ${COLORS.ring}`}
          placeholder="Start typing… e.g. 123 Main St"
          testId={`${testIdPrefix}-primary-line-ac`}
        />
        <input type="hidden" value={value.primary_line || ''} data-testid={`${testIdPrefix}-primary-line`} readOnly />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1">
          Apt / Suite / Unit <span className="text-slate-400 font-normal">(optional)</span>
        </label>
        <input
          type="text"
          value={value.secondary_line || ''}
          onChange={(e) => update({ secondary_line: e.target.value })}
          className={`w-full ${pad} border border-slate-300 rounded-lg ${COLORS.ring}`}
          placeholder="Apt 4B"
          autoComplete="address-line2"
          data-testid={`${testIdPrefix}-secondary-line`}
        />
      </div>
      <div className="grid grid-cols-6 gap-2">
        <div className="col-span-3">
          <label className="block text-sm font-semibold text-slate-700 mb-1">
            City {required && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            value={value.city || ''}
            onChange={(e) => update({ city: e.target.value })}
            className={`w-full ${pad} border border-slate-300 rounded-lg ${COLORS.ring}`}
            placeholder="San Francisco"
            autoComplete="address-level2"
            data-testid={`${testIdPrefix}-city`}
          />
        </div>
        <div className="col-span-1">
          <label className="block text-sm font-semibold text-slate-700 mb-1">
            State {required && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            value={(value.state || '').toUpperCase().slice(0, 2)}
            onChange={(e) => update({ state: e.target.value.toUpperCase().slice(0, 2) })}
            maxLength={2}
            className={`w-full ${pad} border border-slate-300 rounded-lg uppercase ${COLORS.ring}`}
            placeholder="CA"
            autoComplete="address-level1"
            data-testid={`${testIdPrefix}-state`}
          />
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-semibold text-slate-700 mb-1">
            ZIP {required && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            value={value.zip_code || ''}
            onChange={(e) => update({ zip_code: e.target.value })}
            className={`w-full ${pad} border border-slate-300 rounded-lg ${COLORS.ring}`}
            placeholder="94107"
            autoComplete="postal-code"
            data-testid={`${testIdPrefix}-zip`}
          />
        </div>
      </div>
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={handleVerify}
          disabled={verifying}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium text-white ${COLORS.btn} disabled:opacity-50 flex items-center gap-1.5`}
          data-testid={`${testIdPrefix}-verify-btn`}
        >
          {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
          {verifying ? 'Verifying…' : 'Verify address'}
        </button>
      </div>

      {dialogState && (
        <VerificationDialog
          result={dialogState.result}
          original={dialogState.original}
          onAccept={acceptStandardized}
          onKeep={keepOriginal}
          onClose={() => setDialogState(null)}
          colors={COLORS}
          testIdPrefix={testIdPrefix}
        />
      )}
    </div>
  );
}

function VerificationDialog({ result, original, onAccept, onKeep, onClose, colors, testIdPrefix }) {
  const status = result.status;
  const std = result.standardized || {};
  const isOk = status === 'ok';
  const isWarn = status === 'warn';
  const isFail = status === 'fail';
  const isUnknown = status === 'unknown';

  const banner = isOk
    ? { bg: 'bg-emerald-50 border-emerald-200 text-emerald-800', icon: <CheckCircle2 className="w-5 h-5" />, label: 'Address verified — matches a real US address' }
    : isWarn
    ? { bg: 'bg-amber-50 border-amber-200 text-amber-800', icon: <AlertTriangle className="w-5 h-5" />, label: humanizeDeliverability(result.deliverability) }
    : isFail
    ? { bg: 'bg-red-50 border-red-200 text-red-800', icon: <XCircle className="w-5 h-5" />, label: (result.reasons && result.reasons[0]) || 'Address not found — this does not appear to be a real US address' }
    : { bg: 'bg-slate-50 border-slate-200 text-slate-700', icon: <AlertTriangle className="w-5 h-5" />, label: humanizeServiceError(result.error) };

  const sameAsOriginal =
    (std.primary_line || '').toLowerCase() === (original.primary_line || '').toLowerCase() &&
    (std.city || '').toLowerCase() === (original.city || '').toLowerCase() &&
    (std.state || '').toLowerCase() === (original.state || '').toLowerCase() &&
    (std.zip_code || '').replace('-', '') === (original.zip_code || '').replace('-', '');

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={`${testIdPrefix}-verify-dialog`}
    >
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-bold text-slate-900">Address verification</h3>
          <p className="text-sm text-slate-500 mt-0.5">Compare your entry with the standardized version.</p>
        </div>

        <div className={`mx-6 mt-4 px-4 py-3 border rounded-xl flex items-center gap-2 text-sm font-medium ${banner.bg}`} data-testid={`${testIdPrefix}-verify-banner`}>
          {banner.icon}
          <span>{banner.label}</span>
        </div>

        <div className="p-6 grid grid-cols-2 gap-4">
          <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
            <p className="text-xs font-semibold uppercase text-slate-500 mb-2">You entered</p>
            <AddressLines a={original} />
            <button
              type="button"
              onClick={onKeep}
              className="mt-4 w-full px-3 py-2 bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold"
              data-testid={`${testIdPrefix}-keep-original-btn`}
            >
              Keep as entered
            </button>
          </div>
          <div className={`border rounded-xl p-4 ${isFail || isUnknown ? 'bg-slate-50 border-slate-200 opacity-60' : colors.accent}`}>
            <p className="text-xs font-semibold uppercase mb-2 opacity-80">Suggested (standardized)</p>
            {isFail || isUnknown ? (
              <p className="text-sm text-slate-500 italic">No standardized version available.</p>
            ) : (
              <AddressLines a={std} />
            )}
            <button
              type="button"
              onClick={onAccept}
              disabled={isFail || isUnknown || sameAsOriginal}
              className={`mt-4 w-full px-3 py-2 text-white rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${colors.btn}`}
              data-testid={`${testIdPrefix}-accept-suggested-btn`}
            >
              {sameAsOriginal ? 'Already matches' : 'Use suggested'}
            </button>
          </div>
        </div>

        <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-slate-600 hover:text-slate-800"
            data-testid={`${testIdPrefix}-verify-dialog-close`}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function AddressLines({ a }) {
  return (
    <div className="text-sm text-slate-800 space-y-0.5">
      <div>{a.primary_line || <span className="text-slate-400">(no street)</span>}</div>
      {a.secondary_line ? <div>{a.secondary_line}</div> : null}
      <div>
        {a.city}{a.city && (a.state || a.zip_code) ? ', ' : ''}{a.state} {a.zip_code}
      </div>
    </div>
  );
}

function humanizeDeliverability(s) {
  const map = {
    partial_match: 'Close match found — the street or city matched, but the exact house number could not be confirmed',
    deliverable_unnecessary_unit: 'Deliverable — but the apt/suite is unnecessary',
    deliverable_incorrect_unit: 'Deliverable — but the apt/suite number looks wrong',
    deliverable_missing_unit: 'Deliverable — but an apt/suite number is missing',
  };
  return map[s] || `Address may have issues (${s})`;
}

function humanizeServiceError(err) {
  if (!err) return 'Verification service is unavailable — you can still save this address.';
  const e = String(err).toLowerCase();
  if (e.includes('timeout') || e.includes('failed') || e.includes('request')) {
    return 'Could not reach the address verification service. Check your connection and try again in a moment.';
  }
  return `Verification service error: ${err}`;
}

/**
 * Helper: flatten a structured address object to a single display string.
 * Useful for legacy code that stores addresses as a single string column.
 */
export function flattenAddress(a) {
  if (!a) return '';
  if (typeof a === 'string') return a;
  const parts = [
    a.primary_line,
    a.secondary_line,
    [a.city, a.state].filter(Boolean).join(', '),
    a.zip_code,
  ].filter(Boolean);
  return parts.join(', ');
}

/**
 * Helper: parse a single-line address string into structured components.
 * Best-effort; not a replacement for a real parser. Used for legacy migration.
 */
export function parseAddressString(s) {
  if (!s || typeof s !== 'string') return { primary_line: '', secondary_line: '', city: '', state: '', zip_code: '' };
  // Match trailing ZIP and state, then city, then everything else as street.
  const re = /^(.*?),\s*([^,]+?),\s*([A-Za-z]{2})\s+([0-9]{5}(?:-[0-9]{4})?)\s*$/;
  const m = s.trim().match(re);
  if (m) {
    return {
      primary_line: m[1].trim(),
      secondary_line: '',
      city: m[2].trim(),
      state: m[3].toUpperCase(),
      zip_code: m[4],
    };
  }
  return { primary_line: s.trim(), secondary_line: '', city: '', state: '', zip_code: '' };
}
